/**
 * TerraMatrix - Query Results Panel
 * Displays generated queries, LLM context, and data results in a tabbed interface
 */

import React, { useState } from 'react';
import { ChatMessage } from '@/hooks/data/useDataQuery';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Database,
  MessageSquare,
  Code,
  ChartBar,
  Brain,
  Clock,
  FileText,
  AlertCircle,
  Save,
  Bug,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import type { NLQueryDebugInfo, LLMPromptCapture, NLQueryPrompts, SchemaContextDebug } from '@/services/api/dataApi';
import { cn } from '@/lib/utils';
import { SaveTemplateModal } from './SaveTemplateModal';

/**
 * Collapsible section for displaying LLM prompt/response
 */
const PromptSection: React.FC<{ title: string; prompt: LLMPromptCapture }> = ({ title, prompt }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors text-sm font-medium"
      >
        <span>{title}</span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      {isOpen && (
        <div className="p-3 space-y-3 text-xs">
          {/* System Prompt */}
          <div>
            <div className="text-sm font-semibold mb-1 text-purple-400">System Prompt:</div>
            <div className="bg-purple-950/50 border border-purple-500/20 p-2 rounded-lg overflow-x-auto">
              <pre className="font-mono whitespace-pre-wrap text-purple-100">
                {prompt.systemPrompt}
              </pre>
            </div>
          </div>

          {/* User Message */}
          <div>
            <div className="text-sm font-semibold mb-1 text-blue-400">User Message:</div>
            <div className="bg-blue-950/50 border border-blue-500/20 p-2 rounded-lg overflow-x-auto">
              <pre className="font-mono whitespace-pre-wrap text-blue-100">
                {prompt.userMessage}
              </pre>
            </div>
          </div>

          {/* LLM Response */}
          <div>
            <div className="text-sm font-semibold mb-1 text-green-400">LLM Response:</div>
            <div className="bg-green-950/50 border border-green-500/20 p-2 rounded-lg overflow-x-auto">
              <pre className="font-mono whitespace-pre-wrap text-green-100">
                {prompt.rawResponse}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface QueryResultsPanelProps {
  selectedMessage: ChatMessage | null;
  /** Callback when a template is saved */
  onTemplateSaved?: () => void;
}

export const QueryResultsPanel: React.FC<QueryResultsPanelProps> = ({
  selectedMessage,
  onTemplateSaved,
}) => {
  const [activeTab, setActiveTab] = useState('results');
  const [showSaveModal, setShowSaveModal] = useState(false);

  if (!selectedMessage || selectedMessage.role !== 'assistant' || !selectedMessage.debug) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground p-8 text-center">
        <div>
          <Database className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-sm">Run a query to see results and debug info</p>
        </div>
      </div>
    );
  }

  const { debug, data } = selectedMessage;

  // Extract LLM conversation data if available
  const llmConversation = debug.llmConversation || [];
  const llmPrompt = debug.llmPrompt || null;
  const llmResponse = debug.llmResponse || null;

  return (
    <>
    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
      <TabsList className="w-full justify-start px-4 py-6 h-auto rounded-none border-b bg-background">
        <TabsTrigger value="results" className="gap-2">
          <ChartBar className="w-4 h-4" />
          Results
        </TabsTrigger>
        <TabsTrigger value="llm-conversation" className="gap-2">
          <MessageSquare className="w-4 h-4" />
          LLM Conversation
        </TabsTrigger>
        <TabsTrigger value="generated-query" className="gap-2">
          <Code className="w-4 h-4" />
          Generated Query
        </TabsTrigger>
        <TabsTrigger value="intent-analysis" className="gap-2">
          <Brain className="w-4 h-4" />
          Intent Analysis
        </TabsTrigger>
        <TabsTrigger value="debug" className="gap-2">
          <Bug className="w-4 h-4" />
          Debug
        </TabsTrigger>
      </TabsList>

      {/* Results Tab */}
      <TabsContent value="results" className="flex-1 mt-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            {/* Execution Summary */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Execution Summary</CardTitle>
                  <div className="flex items-center gap-2">
                    {debug.executionTimeMs !== undefined && (
                      <Badge variant="secondary" className="gap-1">
                        <Clock className="w-3 h-3" />
                        {debug.executionTimeMs}ms
                      </Badge>
                    )}
                    {selectedMessage.templateQuery && (
                      <button
                        onClick={() => setShowSaveModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                                   rounded-md border border-primary/30 text-primary
                                   hover:bg-primary/10 transition-colors"
                      >
                        <Save className="w-3.5 h-3.5" />
                        Save as Template
                      </button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {selectedMessage.source && (
                  <div>
                    <span className="font-medium">Data Source:</span>{' '}
                    <span className="text-muted-foreground">{selectedMessage.source.name}</span>
                  </div>
                )}
                {data && (
                  <div>
                    <span className="font-medium">Results:</span>{' '}
                    <span className="text-muted-foreground">
                      {Array.isArray(data) ? `${data.length} rows` : 'N/A'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Data Results Table */}
            {data && Array.isArray(data) && data.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Data Results ({data.length} rows)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="border-b">
                          {Object.keys(data[0] || {}).map((key) => (
                            <th key={key} className="text-left p-2 font-medium bg-muted">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.slice(0, 100).map((row: any, index: number) => (
                          <tr key={index} className="border-b hover:bg-muted/50">
                            {Object.values(row).map((value: any, colIndex: number) => (
                              <td key={colIndex} className="p-2">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {data.length > 100 && (
                      <div className="text-center py-2 text-muted-foreground">
                        Showing first 100 of {data.length} rows
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State for Data */}
            {data && Array.isArray(data) && data.length === 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Data Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No data returned from query
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      {/* LLM Conversation Tab */}
      <TabsContent value="llm-conversation" className="flex-1 mt-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  AI Query Generation Process
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* User Query */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-muted-foreground">User Query:</div>
                  <div className="bg-primary/5 p-3 rounded-lg">
                    <p className="text-sm">{selectedMessage.content}</p>
                  </div>
                </div>

                {/* Data Source Matching */}
                {debug.matchedSources && debug.matchedSources.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-muted-foreground">
                      AI Source Matching Process:
                    </div>
                    <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                      {debug.matchedSources.map((source: any, idx: number) => (
                        <div key={idx} className="border-l-2 border-primary/30 pl-3">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">{source.name}</span>
                            <Badge variant={source.canAnswerQuery ? "success" : "secondary"} className="text-xs">
                              {Math.round((source.confidence || source.score || 0) * 100)}% match
                            </Badge>
                          </div>
                          {source.reasoning && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{source.reasoning}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* LLM Prompt (if available) */}
                {llmPrompt && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-muted-foreground">
                      Prompt Sent to LLM:
                    </div>
                    <div className="bg-accent/20 p-3 rounded-lg">
                      <pre className="text-xs whitespace-pre-wrap font-mono">
                        {typeof llmPrompt === 'string' ? llmPrompt : JSON.stringify(llmPrompt, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* LLM Response (if available) */}
                {llmResponse && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-muted-foreground">
                      LLM Response:
                    </div>
                    <div className="bg-success/10 border border-success/20 p-3 rounded-lg">
                      <pre className="text-xs whitespace-pre-wrap font-mono">
                        {typeof llmResponse === 'string' ? llmResponse : JSON.stringify(llmResponse, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Query Generation Explanation */}
                {debug.generatedQuery?.explanation && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-muted-foreground">
                      Query Generation Logic:
                    </div>
                    <div className="bg-blue-500/10 p-3 rounded-lg">
                      <p className="text-sm italic">"{debug.generatedQuery.explanation}"</p>
                    </div>
                  </div>
                )}

                {/* Full LLM Conversation (if available) */}
                {llmConversation.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-muted-foreground">
                      Full AI Conversation:
                    </div>
                    <div className="space-y-2">
                      {llmConversation.map((msg: any, idx: number) => (
                        <div
                          key={idx}
                          className={cn(
                            "p-3 rounded-lg",
                            msg.role === 'system' && "bg-purple-500/10 border border-purple-500/20",
                            msg.role === 'user' && "bg-primary/10 border border-primary/20",
                            msg.role === 'assistant' && "bg-muted border border-border"
                          )}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {msg.role}
                            </Badge>
                            {msg.timestamp && (
                              <span className="text-xs text-muted-foreground">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                          <pre className="text-xs whitespace-pre-wrap">
                            {msg.content}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* If no LLM data is available */}
                {!llmPrompt && !llmResponse && !llmConversation.length && (
                  <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">Limited LLM Data Available</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Detailed LLM conversation tracking may not be enabled.
                          Enable debug mode or check backend configuration for full LLM interaction logs.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </TabsContent>

      {/* Generated Query Tab */}
      <TabsContent value="generated-query" className="flex-1 mt-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            {debug.generatedQuery && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Database Query
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Query Explanation */}
                  {(debug.generatedQuery as any).explanation && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Explanation:</div>
                      <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                        {(debug.generatedQuery as any).explanation}
                      </p>
                    </div>
                  )}

                  {/* Query Confidence */}
                  {(debug.generatedQuery as any).confidence !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Confidence:</span>
                      <Badge
                        variant={(debug.generatedQuery as any).confidence > 0.7 ? "success" : "secondary"}
                      >
                        {Math.round((debug.generatedQuery as any).confidence * 100)}%
                      </Badge>
                    </div>
                  )}

                  {/* Actual Query */}
                  <div>
                    <div className="text-sm font-semibold mb-2">MongoDB Aggregation Pipeline:</div>
                    <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                      <pre className="text-xs font-mono">
                        {typeof (debug.generatedQuery as any).query === 'string'
                          ? (debug.generatedQuery as any).query
                          : JSON.stringify((debug.generatedQuery as any).query || debug.generatedQuery, null, 2)
                        }
                      </pre>
                    </div>
                  </div>

                  {/* Query Type */}
                  {(debug.generatedQuery as any).type && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Query Type:</span>
                      <Badge variant="outline">
                        {(debug.generatedQuery as any).type}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {!debug.generatedQuery && (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">No query information available</p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      {/* Intent Analysis Tab */}
      <TabsContent value="intent-analysis" className="flex-1 mt-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            {debug.intent && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Query Intent Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Intent Type */}
                  {(debug.intent as any).type && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">Query Type:</span>
                      <Badge variant="default">
                        {(debug.intent as any).type}
                      </Badge>
                    </div>
                  )}

                  {/* Extracted Metrics */}
                  {(debug.intent as any).metrics && (debug.intent as any).metrics.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Detected Metrics:</div>
                      <div className="flex flex-wrap gap-2">
                        {(debug.intent as any).metrics.map((metric: string, idx: number) => (
                          <Badge key={idx} variant="secondary">
                            {metric}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Extracted Entities */}
                  {(debug.intent as any).entities && (debug.intent as any).entities.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Detected Entities:</div>
                      <div className="flex flex-wrap gap-2">
                        {(debug.intent as any).entities.map((entity: string, idx: number) => (
                          <Badge key={idx} variant="outline">
                            {entity}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {(debug.intent as any).keywords && (debug.intent as any).keywords.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Keywords:</div>
                      <div className="flex flex-wrap gap-1">
                        {(debug.intent as any).keywords.slice(0, 20).map((keyword: string, idx: number) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 bg-muted rounded-md"
                          >
                            {keyword}
                          </span>
                        ))}
                        {(debug.intent as any).keywords.length > 20 && (
                          <span className="text-xs text-muted-foreground">
                            +{(debug.intent as any).keywords.length - 20} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Time Range */}
                  {(debug.intent as any).timeRange && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Time Range:</div>
                      <div className="bg-muted p-3 rounded-lg">
                        <pre className="text-xs">
                          {JSON.stringify((debug.intent as any).timeRange, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  {(debug.intent as any).filters && Object.keys((debug.intent as any).filters).length > 0 && (
                    <div>
                      <div className="text-sm font-semibold mb-2">Applied Filters:</div>
                      <div className="bg-muted p-3 rounded-lg">
                        <pre className="text-xs">
                          {JSON.stringify((debug.intent as any).filters, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Full Intent Object */}
                  <details className="cursor-pointer">
                    <summary className="text-sm font-semibold text-muted-foreground hover:text-foreground">
                      View Full Intent Object
                    </summary>
                    <div className="mt-2 bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                      <pre className="text-xs font-mono">
                        {JSON.stringify(debug.intent, null, 2)}
                      </pre>
                    </div>
                  </details>
                </CardContent>
              </Card>
            )}

            {/* Matched Sources Analysis */}
            {debug.matchedSources && debug.matchedSources.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Source Matching Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {debug.matchedSources.map((source: any, idx: number) => (
                      <div
                        key={idx}
                        className={cn(
                          "p-3 rounded-lg border",
                          source.canAnswerQuery ? "bg-success/5 border-success/20" : "bg-muted border-border"
                        )}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-semibold text-sm">{source.name}</div>
                            {source.type && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {source.type}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={source.canAnswerQuery ? "success" : "secondary"}
                              className="text-xs"
                            >
                              {Math.round((source.confidence || source.score || 0) * 100)}%
                            </Badge>
                            {source.canAnswerQuery !== undefined && (
                              <Badge
                                variant={source.canAnswerQuery ? "success" : "destructive"}
                                className="text-xs"
                              >
                                {source.canAnswerQuery ? 'Can Answer' : 'Cannot Answer'}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {source.reasoning && (
                          <p className="text-xs text-muted-foreground italic">
                            {source.reasoning}
                          </p>
                        )}
                        {source.matchReasons && source.matchReasons.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {source.matchReasons.map((reason: string, rIdx: number) => (
                              <span key={rIdx} className="text-xs px-1.5 py-0.5 bg-background rounded">
                                {reason}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {!debug.intent && !debug.matchedSources && (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">No intent analysis data available</p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      {/* Debug Tab - LLM Prompts and Schema Context */}
      <TabsContent value="debug" className="flex-1 mt-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-4">
            {/* Schema Context Section */}
            {(debug as NLQueryDebugInfo).schemaContext && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Schema Context ({(debug as NLQueryDebugInfo).schemaContext?.accessibleSchemas.length || 0} schemas)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Accessible Schemas List */}
                  <details className="cursor-pointer">
                    <summary className="text-sm font-semibold flex items-center gap-2 hover:text-primary">
                      <ChevronRight className="w-4 h-4 transition-transform [details[open]>&]:rotate-90" />
                      Accessible Schemas
                    </summary>
                    <div className="mt-2 space-y-2">
                      {(debug as NLQueryDebugInfo).schemaContext?.accessibleSchemas.map((schema, idx) => (
                        <div key={idx} className="p-2 bg-muted rounded-lg text-xs">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{schema.displayName || schema.name}</span>
                            <Badge variant="outline" className="text-xs">{schema.id.slice(-8)}</Badge>
                          </div>
                          {schema.description && (
                            <p className="text-muted-foreground mt-1">{schema.description}</p>
                          )}
                          {schema.tags && schema.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {schema.tags.map((tag, tIdx) => (
                                <span key={tIdx} className="px-1.5 py-0.5 bg-background rounded text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>

                  {/* Schema Index (the actual text sent to LLM) */}
                  <details className="cursor-pointer">
                    <summary className="text-sm font-semibold flex items-center gap-2 hover:text-primary">
                      <ChevronRight className="w-4 h-4 transition-transform [details[open]>&]:rotate-90" />
                      Schema Index (sent to LLM)
                    </summary>
                    <div className="mt-2 bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {(debug as NLQueryDebugInfo).schemaContext?.schemaIndex || 'No schema index available'}
                      </pre>
                    </div>
                  </details>
                </CardContent>
              </Card>
            )}

            {/* LLM Prompts Section */}
            {(debug as NLQueryDebugInfo).prompts && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    LLM Pipeline Prompts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Schema Identification Prompt */}
                  {(debug as NLQueryDebugInfo).prompts?.schemaIdentification && (
                    <PromptSection
                      title="1. Schema Identification"
                      prompt={(debug as NLQueryDebugInfo).prompts!.schemaIdentification!}
                    />
                  )}

                  {/* Intent Analysis Prompt */}
                  {(debug as NLQueryDebugInfo).prompts?.intentAnalysis && (
                    <PromptSection
                      title="2. Intent Analysis"
                      prompt={(debug as NLQueryDebugInfo).prompts!.intentAnalysis!}
                    />
                  )}

                  {/* Field Identification Prompt */}
                  {(debug as NLQueryDebugInfo).prompts?.fieldIdentification && (
                    <PromptSection
                      title="3. Field Identification"
                      prompt={(debug as NLQueryDebugInfo).prompts!.fieldIdentification!}
                    />
                  )}

                  {/* Query Generation Prompt */}
                  {(debug as NLQueryDebugInfo).prompts?.queryGeneration && (
                    <PromptSection
                      title="4. Query Generation"
                      prompt={(debug as NLQueryDebugInfo).prompts!.queryGeneration!}
                    />
                  )}

                  {/* Validation Prompt */}
                  {(debug as NLQueryDebugInfo).prompts?.validation && (
                    <PromptSection
                      title="5. Validation"
                      prompt={(debug as NLQueryDebugInfo).prompts!.validation!}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step Timings */}
            {(debug as NLQueryDebugInfo).stepTimings && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Pipeline Execution Timing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries((debug as NLQueryDebugInfo).stepTimings || {}).map(([step, time]) => (
                      <div key={step} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground capitalize">
                          {step.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <Badge variant={time > 1000 ? 'destructive' : time > 500 ? 'secondary' : 'default'}>
                          {time}ms
                        </Badge>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm pt-2 border-t">
                      <span className="font-semibold">Total</span>
                      <Badge variant="default">
                        {(debug as NLQueryDebugInfo).executionTimeMs}ms
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Debug Info */}
            {!(debug as NLQueryDebugInfo).prompts && !(debug as NLQueryDebugInfo).schemaContext && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                  <AlertCircle className="w-8 h-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Debug information not available.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enable debug mode by adding <code className="bg-muted px-1 rounded">includeDebug: true</code> to your query.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

    </Tabs>

    {/* Save Template Modal */}
    {selectedMessage?.templateQuery && (
      <SaveTemplateModal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        templateQuery={selectedMessage.templateQuery}
        templateVariables={selectedMessage.templateVariables}
        onSaved={() => {
          setShowSaveModal(false);
          onTemplateSaved?.();
        }}
      />
    )}
    </>
  );
};

export default QueryResultsPanel;